import { backendApi } from "./backendApi.rtkq";

export interface OfficeMember {
  userId: string;
  email: string;
  displayName: string;
  role: string;
  isOwner: boolean;
  lastLoginAt?: string;
  joinedAt?: string;
}

export interface OfficeInvitation {
  id: string;
  email: string;
  role: string;
  token: string;
  status: string;
  expiresAt: string;
  acceptUrl: string;
  createdAt: string;
}

export interface OfficeDashboardAppLimit {
  appIdentifier: string;
  name: string;
  includedCredits: number;
  remainingCredits: number | null;
  usedCredits: number;
  creditCost: number;
  usageLimits: {
    daily?: number | null;
    monthly: number | null;
    window?: number | null;
  };
  bonusCredits?: number;
  usedToday: {
    operations: number;
    credits: number;
  };
  usedThisMonth: {
    operations: number;
    credits: number;
  };
}

export interface OfficeDashboard {
  office: {
    id: string;
    name: string;
    planName?: string | null;
    subscriptionStatus: string;
    creditBalance: number;
    monthlyCreditAllocation?: number;
    appCreditBalances: Record<string, number>;
    appBonusCreditBalances?: Record<string, number>;
    quotaWindowEnd?: string | null;
    currentPeriodEnd?: string | null;
  };
  members: {
    total: number;
    admins: number;
    users: number;
    list: OfficeMember[];
  };
  cases: {
    total: number;
    active: number;
    archived: number;
    recentlyUpdated: any[];
  };
  usage: {
    totalCreditsConsumed: number;
    creditsByApp: Record<string, number>;
    apps?: OfficeDashboardAppLimit[];
  };
}

export const officesApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    // Office members
    getOfficeMembers: builder.query<
      { officeId: string; ownerUserId: string; members: OfficeMember[] },
      string
    >({
      query: (officeId) => `/offices/${officeId}/members`,
      providesTags: ["Members"],
    }),

    updateMemberRole: builder.mutation<
      any,
      { officeId: string; userId: string; role: string }
    >({
      query: ({ officeId, userId, role }) => ({
        url: `/offices/${officeId}/members/${userId}/role`,
        method: "PUT",
        body: { role },
      }),
      invalidatesTags: ["Members"],
    }),

    removeMember: builder.mutation<any, { officeId: string; userId: string }>({
      query: ({ officeId, userId }) => ({
        url: `/offices/${officeId}/members/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Members"],
    }),

    // Office settings
    updateOfficeSettings: builder.mutation<
      any,
      { officeId: string; name?: string; accountInformation?: Record<string, any> }
    >({
      query: ({ officeId, ...body }) => ({
        url: `/offices/${officeId}/settings`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Accounts"],
    }),

    transferOwnership: builder.mutation<
      any,
      { officeId: string; newOwnerUserId: string }
    >({
      query: ({ officeId, newOwnerUserId }) => ({
        url: `/offices/${officeId}/transfer-ownership`,
        method: "POST",
        body: { newOwnerUserId },
      }),
      invalidatesTags: ["Members", "Accounts"],
    }),

    deleteOffice: builder.mutation<any, string>({
      query: (officeId) => ({
        url: `/offices/${officeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Accounts"],
    }),

    // Invitations
    sendInvitation: builder.mutation<
      OfficeInvitation,
      { officeId: string; email: string; role?: string }
    >({
      query: ({ officeId, email, role }) => ({
        url: `/offices/${officeId}/invitations`,
        method: "POST",
        body: { email, role },
      }),
      invalidatesTags: ["Invitations"],
    }),

    getInvitations: builder.query<OfficeInvitation[], string>({
      query: (officeId) => `/offices/${officeId}/invitations`,
      providesTags: ["Invitations"],
    }),

    cancelInvitation: builder.mutation<
      any,
      { officeId: string; invitationId: string }
    >({
      query: ({ officeId, invitationId }) => ({
        url: `/offices/${officeId}/invitations/${invitationId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Invitations"],
    }),

    getInvitationByToken: builder.query<
      { id: string; email: string; role: string; officeName: string; expiresAt: string },
      string
    >({
      query: (token) => `/invitations/${token}`,
    }),

    acceptInvitation: builder.mutation<
      { accepted: boolean; officeId: string; officeName: string; role: string },
      string
    >({
      query: (token) => ({
        url: `/invitations/${token}/accept`,
        method: "POST",
      }),
      invalidatesTags: ["Members", "Accounts"],
    }),

    // Dashboard
    getOfficeDashboard: builder.query<OfficeDashboard, string>({
      query: (officeId) => `/offices/${officeId}/dashboard`,
      providesTags: ["Dashboard"],
    }),

    // Activity log
    getActivityLog: builder.query<any[], string>({
      query: (officeId) => `/offices/${officeId}/activity`,
    }),
  }),
});

export const {
  useGetOfficeMembersQuery,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
  useUpdateOfficeSettingsMutation,
  useTransferOwnershipMutation,
  useDeleteOfficeMutation,
  useSendInvitationMutation,
  useGetInvitationsQuery,
  useCancelInvitationMutation,
  useGetInvitationByTokenQuery,
  useAcceptInvitationMutation,
  useGetOfficeDashboardQuery,
  useGetActivityLogQuery,
} = officesApi;
